import { useEffect, useLayoutEffect } from 'react';

import { beginProgrammaticScroll } from '@designsystem/stories/01-core/ProgrammaticScroll';

interface HashScrollHandlerProps {
  pathname: string;
  content: any;
}

const useIsomorphicLayoutEffect =
  typeof window !== 'undefined' ? useLayoutEffect : useEffect;

/**
 * Scrolls to the anchor element identified by the URL hash fragment.
 *
 * On SSR pages, the browser may scroll to the anchor natively, but
 * subsequent layout shifts from hydration, CSS loading, or async
 * content can move the anchor away from the viewport. This component
 * re-scrolls to the anchor after React commits DOM updates.
 *
 * Uses useLayoutEffect for the initial scroll (runs synchronously
 * before the browser paints, avoiding visible flicker) and useEffect
 * for re-scrolling when content changes asynchronously.
 */
const HashScrollHandler = ({ pathname, content }: HashScrollHandlerProps) => {
  const scrollToHash = () => {
    const hash = window.location.hash;
    if (!hash) return;

    const id = decodeURIComponent(hash.substring(1));
    if (!id) return;

    const el =
      document.getElementById(id) ||
      document.querySelector(`a[name="${CSS.escape(id)}"]`);
    if (el) {
      // Announce it first: for the header this scroll is indistinguishable
      // from a user scroll, and it would arrive with the anchor navigation
      // already pushed off-screen (#1778).
      beginProgrammaticScroll();
      el.scrollIntoView({ behavior: 'auto' });
    }
  };

  // Initial scroll: useLayoutEffect runs synchronously after DOM
  // mutations but before the browser paints, so the user never
  // sees the wrong scroll position.
  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined' || !window.location.hash) return;
    scrollToHash();
  }, [pathname]);

  // Re-scroll when content changes (handles async content loading
  // that shifts the layout after initial render).
  useEffect(() => {
    if (typeof window === 'undefined' || !window.location.hash) return;

    // Use requestAnimationFrame to run after the browser has
    // laid out the new content from the React commit.
    const rafId = requestAnimationFrame(() => scrollToHash());
    return () => cancelAnimationFrame(rafId);
  }, [content]);

  return null;
};

export default HashScrollHandler;
